export const englishCourse = [
    {
        "metadata": {
            "id": "english_ks2_week1A_slides",
            "classTitle": "English",
            "keyStage": 2,
            "week": 1,
            "order": 1,
            "module": "Narrative Writing",
            "subheading": "Narrative Writing",
            "lesson": "Week 1A",
            "yearGroup": "Year 6",
            "title": "Exploring Multiple Perspectives in Narrative Writing",
            "description": "Understanding how narrative point of view (POV) changes a story, using AI as a tool to experiment with perspective.",
            "curriculumAlignment": "KS2 Writing – Composition: write for real purposes and audiences; in narratives, describe settings, characters and atmosphere. KS2 Spoken Language: consider different viewpoints and participate in collaborative conversations."
        },
        "slides": [
            {
                "title": "👋 Welcome Writers!",
                "content": "Today we explore how point of view changes a story.",
                "image": "story_opening_scene.jpg",
                "speakerNotes": {
                    "objective": "Introduce the day's focus: how stories change when told by different characters.",
                    "questions": ["Can two people see the same event differently?"],
                    "imagePrompt": "Illustration of three characters watching the same moment from different angles.",
                    "scaffoldingTips": {
                        "support": "Use emojis to label character feelings.",
                        "core": "Connect this to real-life disagreements or different memories.",
                        "challenge": "Ask students to predict how different people might tell a news story."
                    },
                    "teacherTip": "Model curiosity and empathy early in the lesson."
                }
            },
            {
                "title": "🧠 What is Perspective in Writing?",
                "content": "Perspective = WHO is telling the story.\nIt changes:\n- What details are shown\n- How the story feels\n- What the reader believes",
                "image": "narrator_explained.jpg",
                "speakerNotes": {
                    "objective": "Define narrative POV and why it matters.",
                    "questions": ["What happens if a villain tells the story?", "What would the ice cream say?"],
                    "imagePrompt": "A character telling a story with others listening behind them, each thinking something different.",
                    "scaffoldingTips": {
                        "support": "Give sentence starters: 'I saw...', 'They felt...'.",
                        "core": "Match short texts to narrators.",
                        "challenge": "Ask: Who’s hiding something in their version?"
                    },
                    "teacherTip": "Dramatize a short monologue with two voices to show tone shift."
                }
            },
            {
                "title": "🧭 Types of Narrators",
                "content": "1st Person: I, me, my\n3rd Person Limited: he/she knows one character\n3rd Person Omniscient: knows everyone’s thoughts",
                "image": "narrator_types_chart.jpg",
                "speakerNotes": {
                    "objective": "Clarify key types of narrative voice.",
                    "questions": ["Which POV feels the most personal?"],
                    "imagePrompt": "Chart of narrator types with sample sentence under each.",
                    "scaffoldingTips": {
                        "support": "Match labels to short excerpts.",
                        "core": "Sort example sentences by POV.",
                        "challenge": "Rewrite the same line in all 3 perspectives."
                    },
                    "teacherTip": "Ask students to act out a scene, then narrate it as different narrators."
                }
            },
            {
                "title": "🎭 The Ice Cream Incident",
                "content": "A child drops an ice cream. Three characters see it:\n- The child\n- A dog\n- The vendor\nWhat would each say?",
                "image": "icecream_scene.jpg",
                "speakerNotes": {
                    "objective": "Introduce shared visual moment for narrative rewriting.",
                    "questions": ["Who has the biggest feeling about this?", "Who is watching quietly?"],
                    "imagePrompt": "Cartoon of a dropped ice cream, child sad, dog excited, vendor shocked.",
                    "scaffoldingTips": {
                        "support": "Point to each character and brainstorm a feeling.",
                        "core": "Write what each might say in a bubble.",
                        "challenge": "What does each character notice that others don’t?"
                    },
                    "teacherTip": "This becomes the writing base for the rest of the lesson."
                }
            },
            {
                "title": "🤖 What Can AI Do With Perspective?",
                "content": "AI can:\n- Rewrite a scene\n- Take on a character’s voice\n- Surprise us with unusual ideas\nBut it can’t feel or judge fairness.",
                "image": "ai_vs_human_story.jpg",
                "speakerNotes": {
                    "objective": "Position AI as a creative but limited storytelling tool.",
                    "questions": ["What makes your version feel more real than AI’s?"],
                    "imagePrompt": "Robot writing a story, a child looking skeptical.",
                    "scaffoldingTips": {
                        "support": "Chart: AI can / AI can’t.",
                        "core": "Compare two sentences.",
                        "challenge": "Have students critique AI tone or details."
                    },
                    "teacherTip": "Show AI as a spark, not a solution."
                }
            },
            {
                "title": "🤝 Can AI Be Creative?",
                "content": "AI can help us imagine.\nBut YOU bring real experience and emotion.\nTogether, we can merge ideas to tell stories in powerful ways.\n\nLet’s explore if AI can truly create — or just remix.",
                "image": "ai_and_human_merge.jpg",
                "speakerNotes": {
                    "objective": "Encourage thoughtful debate: is AI creative?",
                    "questions": ["What makes something truly creative?", "Can a machine know how it feels to be left out or joyful?"],
                    "imagePrompt": "A child and robot drawing on a shared storybook page.",
                    "scaffoldingTips": {
                        "support": "Give emotional vocabulary bank.",
                        "core": "Chart the differences between experience and imagination.",
                        "challenge": "Ask: Could AI ever write your life story truthfully?"
                    },
                    "teacherTip": "Frame creativity as part human spark, part tool use — a blend of thinking and feeling."
                }
            },
            {
                "title": "💬 Let’s Ask AIDEN GPT!",
                "content": "Prompt: ‘Describe the ice cream scene from the POV of the dog.’\nResult: ‘Cold, sweet glory on the pavement. I ran, tail wagging. The small human wailed. I dreamed of a lick.’",
                "image": "ai_pov_output_dog.jpg",
                "speakerNotes": {
                    "objective": "Demonstrate AI generation live.",
                    "questions": ["What details did it focus on?", "Was it funny or emotional?"],
                    "imagePrompt": "Dog’s eye view of dropped ice cream.",
                    "aiPrompt": "Describe the same moment from the perspective of the ice cream.",
                    "scaffoldingTips": {
                        "support": "Use sentence frames.",
                        "core": "Highlight what the dog noticed.",
                        "challenge": "Ask students to revise it with more feeling."
                    },
                    "teacherTip": "Let students guess what AI might say first."
                }
            },
            {
                "title": "✍️ Time to Write: From 3 Points of View (10 min)",
                "content": "Write the scene from:\n1. Your own perspective\n2. A partner’s rewritten version\n3. AI’s character suggestion (e.g., the cone or an ant!)",
                "image": "students_writing_pov.jpg",
                "speakerNotes": {
                    "objective": "Practice shifting perspective in written form.",
                    "questions": ["Which version felt hardest to write?"],
                    "imagePrompt": "Students working in small groups, writing on tablets or notebooks.",
                    "scaffoldingTips": {
                        "support": "Use sentence starters.",
                        "core": "Students work in pairs to draft and swap.",
                        "challenge": "Groups merge all perspectives into one creative version."
                    },
                    "teacherTip": "Ask students to take out paper and write by hand. This deepens engagement and slows thinking."
                }
            },
            {
                "title": "🎯 Metacognitive Reflection",
                "content": "Let’s reflect:\n- Which perspective felt most emotional?\n- What did you notice about your writing vs AI’s?\n- When does changing perspective deepen meaning?",
                "image": "reflection_circle.jpg",
                "speakerNotes": {
                    "objective": "Solidify learning and activate reflective thinking.",
                    "questions": ["Did AI ever surprise you?"],
                    "imagePrompt": "Students sitting in a circle, discussing or posting on a wall.",
                    "scaffoldingTips": {
                        "support": "Use visual feelings prompts.",
                        "core": "Sticky note insights: ‘I used to think... now I think...’",
                        "challenge": "Write a journal entry comparing all 3 versions."
                    },
                    "teacherTip": "Celebrate insight over accuracy. This is identity-building work."
                }
            },
            {
                "title": "📚 Homework: Flip the Perspective",
                "content": "Choose a scene from a book you’re reading.\nRewrite it from a different character’s or object’s POV.\nUse AIDEN GPT to compare your version.\nBring both to class next week.\n\n✨ NEW: Log your reflection in your AIDEN Journal: \n- What did you change? \n- What did you learn from AI’s version?",
                "image": "homework_writing_book.jpg",
                "speakerNotes": {
                    "objective": "Extend learning beyond the classroom.",
                    "questions": ["What will make your version unique?"],
                    "imagePrompt": "Student writing at home with a book and laptop side by side.",
                    "scaffoldingTips": {
                        "support": "Provide a list of familiar books to choose scenes from.",
                        "core": "Students choose any scene they love.",
                        "challenge": "Add emotion or tone to change the meaning of the scene."
                    },
                    "teacherTip": "Encourage students to complete their AIDEN journal entry after comparing the two versions. This helps build reflective habits."
                }
            }
        ],
        "teacherMaterials": [
            {
                "name": "Lesson Plan & Objectives",
                "componentType": "lessonplanObjectives",
                "content": [
                    {
                        "sectionName": "Learning Objectives",
                        "title": "What Students Will Learn",
                        "description": "By the end of this lesson, students will:\n✅ Understand how point of view changes narrative.\n✅ Identify first-person, third-person limited, and omniscient narrators.\n✅ Use AI to explore alternative viewpoints.\n✅ Reflect on how perspective affects tone, emotion, and storytelling.",
                        "icon": "TargetIcon"
                    }
                ],
                "lessonPlan": {
                    "sections": [
                        {
                            "part": 1,
                            "name": "Starter Activity: One Scene, Many Views",
                            "duration": 10,
                            "activities": [
                                {
                                    "icon": "GroupIcon",
                                    "primary": "Present scene: 'Child drops ice cream.'",
                                    "secondary": "Students brainstorm how it’s told from three viewpoints."
                                }
                            ]
                        },
                        {
                            "part": 2,
                            "name": "Concept Exploration: What is Perspective?",
                            "duration": 10,
                            "activities": [
                                {
                                    "icon": "PresentationIcon",
                                    "primary": "Explain first- vs third-person and limited vs omniscient.",
                                    "secondary": "Use examples and short passages for comparison."
                                },
                                {
                                    "icon": "GroupIcon",
                                    "primary": "Group activity with three stations.",
                                    "secondary": "Students rotate and write the same scene from different perspectives."
                                }
                            ]
                        },
                        {
                            "part": 3,
                            "name": "Live AI Demonstration: Perspective Generation",
                            "duration": 5,
                            "activities": [
                                {
                                    "icon": "AIIcon",
                                    "primary": "Use AIDEN GPT to generate a version from the dog’s POV.",
                                    "secondary": "Discuss tone, focus, and emotional weight in the AI output."
                                }
                            ]
                        },
                        {
                            "part": 4,
                            "name": "Student Writing: Independent + Group Task",
                            "duration": 10,
                            "activities": [
                                {
                                    "icon": "WritingIcon",
                                    "primary": "Students write the scene by hand from their own POV.",
                                    "secondary": "Swap and rewrite from another character's perspective."
                                },
                                {
                                    "icon": "AIIcon",
                                    "primary": "Use AIDEN GPT to generate a third perspective.",
                                    "secondary": "Group discussion of how each version differs."
                                }
                            ]
                        },
                        {
                            "part": 5,
                            "name": "Plenary: Reflection & Meaning",
                            "duration": 5,
                            "activities": [
                                {
                                    "icon": "DiscussionIcon",
                                    "primary": "Discuss how perspective changes the story’s impact.",
                                    "secondary": "Students post sticky notes with one metacognitive insight."
                                }
                            ]
                        }
                    ]
                }
            },
            {
                "name": "Assessment & Homework",
                "componentType": "assessment",
                "content": [
                    {
                        "sectionName": "Assessment Criteria",
                        "title": "Evaluating Perspective Writing",
                        "description": "Students will be assessed on their ability to:\n✔️ Identify and write from different points of view\n✔️ Use tone and detail to show perspective shift\n✔️ Incorporate AI output in writing comparison\n✔️ Reflect with clarity on their own and AI-generated work",
                        "icon": "CheckIcon"
                    },
                    {
                        "sectionName": "Homework Task",
                        "title": "Perspective Rewrite & AIDEN Journal",
                        "description": "Choose a scene from a book you're reading.\nRewrite it from a different POV (another character or object).\nUse AIDEN GPT to generate a second version.\n📝 Log your thoughts in your AIDEN Journal:\n- What changed?\n- What surprised you?\n- What did the AI miss or exaggerate?",
                        "icon": "BookIcon"
                    }
                ]
            },
            {
                "name": "Metacognitive Reflection",
                "componentType": "metacognitiveExercise",
                "content": [
                    {
                        "sectionName": "In-Class Reflection Prompt",
                        "title": "How Perspective Shapes Meaning",
                        "description": "After writing from multiple viewpoints, students complete one of the following:\n📝 Sticky Note Reflection:\n- 'I used to think... now I think...'\n- 'The AI helped me see...'\n\n📣 Sharing Circle:\n- Share one surprise from your writing or AI’s.\n- What felt more emotional: your story or AI’s? Why?",
                        "icon": "ReflectionIcon"
                    },
                    {
                        "sectionName": "AIDEN Journal Integration",
                        "title": "Weekly Journal Entry",
                        "description": "Students complete their first AIDEN Journal entry at home or in class:\n- What perspective did I write from?\n- What did I learn by rewriting it?\n- How did AI’s version compare to mine?",
                        "icon": "NotebookIcon"
                    }
                ]
            }
        ]
    },

    {
        "metadata": {
            "id": "english_ks2_week1B_slides",
            "classTitle": "English",
            "keyStage": 2,
            "week": 1,
            "order": 2,
            "module": "Narrative Writing",
            "subheading": "Narrative Writing",
            "lesson": "Week 1B",
            "yearGroup": "Year 6",
            "title": "Character Development with AI",
            "description": "Create believable characters with rich traits and motivations, using AI as a creative co-author.",
            "curriculumAlignment": "KS2 Writing – Composition: In narratives, describe settings, characters and atmosphere. KS2 Spoken Language: Participate in collaborative conversations and roleplay."
        },
        "slides": [
            {
                "title": "🎭 Welcome to Character Lab!",
                "content": "This week, we bring characters to life — with a little help from AI.",
                "image": "character_intro.jpg",
                "speakerNotes": {
                    "objective": "Set the tone for building strong fictional characters.",
                    "questions": ["What makes a character feel real to you?"],
                    "imagePrompt": "A group of illustrated characters from different genres lined up on stage.",
                    "scaffoldingTips": {
                        "support": "Use a character puppet or toy.",
                        "core": "Connect to books students are reading.",
                        "challenge": "Ask students to define 'believable'."
                    },
                    "teacherTip": "Excite students about creative invention and AI’s role."
                }
            },
            {
                "title": "❓ Starter: Who Am I?",
                "content": "Clue 1: I move in shadows, but love the stars.\nClue 2: I speak little, but notice everything.\nClue 3: I guard a secret map in my cloak.\n\nWho am I?",
                "image": "guess_character_game.jpg",
                "speakerNotes": {
                    "objective": "Warm up imagination through descriptive inference.",
                    "questions": ["What traits do you hear in these clues?"],
                    "imagePrompt": "A mysterious silhouette of a cloaked figure under starlight.",
                    "scaffoldingTips": {
                        "support": "Offer visual choices for guesses.",
                        "core": "Students write one sentence about what they think this person wants.",
                        "challenge": "Have students invent their own 'Who Am I' riddle."
                    },
                    "teacherTip": "Use expressive tone and pacing — build intrigue!"
                }
            },
            {
                "title": "🔍 What Makes a Great Character?",
                "content": "Characters are more than looks:\n✔️ Desires — what they want\n✔️ Fears — what they avoid\n✔️ Secrets — what they hide\n✔️ Growth — how they might change",
                "image": "character_traits_chart.jpg",
                "speakerNotes": {
                    "objective": "Introduce depth elements that make characters feel real.",
                    "questions": ["Which of these is most important in your favorite characters?"],
                    "imagePrompt": "A side-by-side list of four character traits with simple icons.",
                    "scaffoldingTips": {
                        "support": "Match sample character traits to familiar characters.",
                        "core": "Classify traits based on movie/book examples.",
                        "challenge": "Create a character who breaks a stereotype."
                    },
                    "teacherTip": "Use visuals, anchor charts, or sketch notes to scaffold."
                }
            },
            {
                "title": "📝 Character Profile Template",
                "content": "Let’s sketch a new character:\n- Name\n- Age\n- Appearance\n- Strength\n- Flaw\n- Dream\n- Secret",
                "image": "character_profile_blank.jpg",
                "speakerNotes": {
                    "objective": "Help students structure ideas clearly.",
                    "questions": ["What makes this character different from others?"],
                    "imagePrompt": "Worksheet or digital card with profile fields and doodles.",
                    "scaffoldingTips": {
                        "support": "Use pre-filled cards with only one blank space.",
                        "core": "Students fill profiles based on a genre.",
                        "challenge": "Students add how this character might change."
                    },
                    "teacherTip": "Print or project these as anchor tools during AI writing."
                }
            },
            {
                "title": "🤖 AI Character Creation Demo",
                "content": "Prompt: 'Create a fantasy character who is brave but hiding something.'\n\nAI says: Lina, age 11, speaks to dragons but no one must know. Her dream: to fly. Her fear: being locked away.",
                "image": "ai_character_output.jpg",
                "speakerNotes": {
                    "objective": "Show AI as a creative launchpad, not the end of the process.",
                    "questions": ["Does this character feel real? Why or why not?"],
                    "imagePrompt": "Child in fantasy setting whispering to a dragon.",
                    "scaffoldingTips": {
                        "support": "Provide sentence stems: 'Lina is... She fears...'.",
                        "core": "Ask students what part surprised them.",
                        "challenge": "Have students rewrite Lina to fit a sci-fi setting."
                    },
                    "teacherTip": "Ask students to critique, revise, or extend AI’s idea."
                }
            },
            {
                "title": "🛠 Group Activity: Build + Act Your Character",
                "content": "1. Build a character profile in pairs\n2. Use AI for ideas OR invent freely\n3. Act out a mini-scene: ‘What would they do if they lost something precious?’",
                "image": "student_group_roleplay.jpg",
                "speakerNotes": {
                    "objective": "Bring writing to life with embodied imagination.",
                    "questions": ["How would your character move, sound, react?"],
                    "imagePrompt": "Students roleplaying in character costumes or using gestures.",
                    "scaffoldingTips": {
                        "support": "Script first line together.",
                        "core": "Use character profile as prompt.",
                        "challenge": "Add a twist mid-scene and adapt on the spot."
                    },
                    "teacherTip": "Use low-stakes drama: acting, narration, or voice-over depending on comfort."
                }
            },
            {
                "title": "💡 Metacognitive Circle: Who Did We Become?",
                "content": "Reflect with your class:\n- What made your character believable?\n- What did AI give you that surprised you?\n- What could only you bring to the story?",
                "image": "student_circle_reflection.jpg",
                "speakerNotes": {
                    "objective": "Encourage critical thinking about creative process.",
                    "questions": ["Who helped you understand your character better: your partner or the AI?"],
                    "imagePrompt": "Students sitting in a circle with journals or reflection notes.",
                    "scaffoldingTips": {
                        "support": "Use reflection starters: 'I discovered...'.",
                        "core": "Turn & talk, then share to board.",
                        "challenge": "Journal: ‘I used to think characters were made up… now I think…’."
                    },
                    "teacherTip": "Value each student’s identity as a growing storyteller."
                }
            },
            {
                "title": "📚 Homework: Character Across Genres",
                "content": "1. Ask AIDEN GPT to create a character in a *new genre*\n2. Write a diary entry from their perspective\n3. AIDEN Journal:\n - What changed about their voice or fears?\n- What stayed the same ? ",
                "image": "homework_character_journal.jpg",
                "speakerNotes": {
                    "objective": "Encourage transfer of skill to new genres and voices.",
                    "questions": ["What does genre change about how a character thinks or speaks?"],
                    "imagePrompt": "Diary split into sci-fi, mystery, and fantasy examples.",
                    "scaffoldingTips": {
                        "support": "Provide diary sentence starters.",
                        "core": "Checklist: motive, flaw, emotion.",
                        "challenge": "Ask: What does this character reveal that they wouldn’t say out loud?"
                    },
                    "teacherTip": "Encourage hand-written entries first, then AI reflection."
                }
            }
        ],
        "teacherMaterials": [
            {
                "name": "Lesson Plan & Objectives",
                "componentType": "lessonplanObjectives",
                "content": [
                    {
                        "sectionName": "Learning Objectives",
                        "title": "What Students Will Learn",
                        "description": "By the end of this lesson, students will:\n✅ Build believable characters using structured templates.\n✅ Explore how character motivation and flaws affect story arcs.\n✅ Use AI to co-create character profiles.\n✅ Collaborate and roleplay to deepen understanding of voice and dialogue.",
                        "icon": "TargetIcon"
                    }
                ],
                "lessonPlan": {
                    "sections": [
                        {
                            "part": 1,
                            "name": "Starter Activity: Character Guess Who?",
                            "duration": 5,
                            "activities": [
                                {
                                    "icon": "GroupIcon",
                                    "primary": "Teacher performs 3 mysterious character descriptions.",
                                    "secondary": "Students guess character personality using clues."
                                }
                            ]
                        },
                        {
                            "part": 2,
                            "name": "Concept Exploration: What Makes a Great Character?",
                            "duration": 10,
                            "activities": [
                                {
                                    "icon": "PresentationIcon",
                                    "primary": "Introduce core traits: desire, fear, secret, growth.",
                                    "secondary": "Students brainstorm examples from books or shows."
                                },
                                {
                                    "icon": "WorksheetIcon",
                                    "primary": "Distribute character profile template.",
                                    "secondary": "Groups begin filling in a sample character together."
                                }
                            ]
                        },
                        {
                            "part": 3,
                            "name": "AI Demonstration: Generating Character Profiles",
                            "duration": 10,
                            "activities": [
                                {
                                    "icon": "AIIcon",
                                    "primary": "Use AIDEN GPT to create a character from a prompt.",
                                    "secondary": "Class analyzes AI's strength and gaps in personality realism."
                                }
                            ]
                        },
                        {
                            "part": 4,
                            "name": "Group Work: Build + Roleplay a Character",
                            "duration": 15,
                            "activities": [
                                {
                                    "icon": "CollaborationIcon",
                                    "primary": "Students co-develop a character profile using AI or their own ideas.",
                                    "secondary": "Groups act out short roleplay scenes."
                                }
                            ]
                        },
                        {
                            "part": 5,
                            "name": "Plenary: Reflection & Meaning",
                            "duration": 5,
                            "activities": [
                                {
                                    "icon": "DiscussionIcon",
                                    "primary": "Whole-class reflection circle.",
                                    "secondary": "Prompt: 'What made your character feel real?'"
                                }
                            ]
                        }
                    ]
                }
            },
            {
                "name": "Assessment & Homework",
                "componentType": "assessment",
                "content": [
                    {
                        "sectionName": "Assessment Criteria",
                        "title": "Evaluating Character Work",
                        "description": "Students will be assessed on their ability to:\n✔️ Create a character with believable traits and depth.\n✔️ Use descriptive language and voice in writing and performance.\n✔️ Use AI support constructively.\n✔️ Participate thoughtfully in group work and reflection.",
                        "icon": "CheckIcon"
                    },
                    {
                        "sectionName": "Homework Task",
                        "title": "New Character, New Genre",
                        "description": "Use AIDEN GPT to generate a new character in a different genre.\nWrite a diary entry from that character’s perspective.\n📝 In your AIDEN Journal, reflect:\n- How did the genre shift change the character?\n- What details felt real or forced?",
                        "icon": "BookIcon"
                    }
                ]
            },
            {
                "name": "Metacognitive Reflection",
                "componentType": "metacognitiveExercise",
                "content": [
                    {
                        "sectionName": "In-Class Reflection Prompt",
                        "title": "Discovering Your Character",
                        "description": "After roleplay, students write on a sticky note:\n- 'I realised my character was... when they...'\n- 'The AI gave me the idea for... but I changed...'\nStudents post on the reflection wall.",
                        "icon": "ReflectionIcon"
                    },
                    {
                        "sectionName": "AIDEN Journal Entry",
                        "title": "Voice, Emotion, and Realism",
                        "description": "Journal Prompt:\n- What helped your character come alive?\n- Did the AI spark something unexpected?\n- What does voice sound like when it’s authentic?",
                        "icon": "NotebookIcon"
                    }
                ]
            }
        ]
    },

    {
        "metadata": {
            "id": "english_ks2_week2A",
            "classTitle": "English",
            "keyStage": 2,
            "week": 2,
            "order": 3,
            "module": "Narrative Writing",
            "subheading": "Narrative Writing",
            "lesson": "Week 2A",
            "yearGroup": "Year 6",
            "title": "Structuring Plot with Conflict and Resolution",
            "description": "Explore how story conflict builds tension and resolution creates meaning, using AI to scaffold plot design.",
            "curriculumAlignment": "KS2 Writing – Composition: Draft and write narratives with clear structure, including a problem and resolution. KS2 Reading – Comprehension: Identify how authors structure narratives for effect."
        },
        "slides": [
            {
                "title": "📚 Welcome to Story Building!",
                "content": "This week we’re diving into plot — the heartbeat of every story!",
                "image": "story_plot_intro.jpg",
                "speakerNotes": {
                    "objective": "Get students excited about plot and purpose.",
                    "questions": ["What makes you want to keep reading a story?"],
                    "imagePrompt": "Open book with rising mountain labeled ‘conflict’ and flag on top marked ‘resolution’.",
                    "scaffoldingTips": {
                        "support": "Refer to familiar stories or movies.",
                        "core": "Ask: What is the most exciting part of a story?",
                        "challenge": "Ask: Is every story supposed to end happily?"
                    },
                    "teacherTip": "Anchor the goal: understand tension, pacing, and change."
                }
            },
            {
                "title": "🔀 Starter: Story Mash-Up!",
                "content": "Imagine a story that has: \n🗝 A lost key\n👵 A grumpy grandma\n🚀 A spaceship\n        What’s the problem?",
                "image": "story_elements_mashup.jpg",
                "speakerNotes": {
                    "objective": "Warm-up creative thinking and introduce conflict.",
                    "questions": ["Who has the problem? How might it end?"],
                    "imagePrompt": "Cartoon of each item: key, grandma, rocket ship.",
                    "scaffoldingTips": {
                        "support": "Give sentence starter: 'The problem is...'.",
                        "core": "Students create a one-sentence conflict.",
                        "challenge": "Write two possible endings to the scene."
                    },
                    "teacherTip": "Use humor to ease pressure. Silly ideas welcome!"
                }
            },
            {
                "title": "📈 Plot Mountain: The Shape of Stories",
                "content": "Stories rise and fall like mountains:\n1. Beginning\n2. Build-up\n3. Problem (Conflict)\n4. Resolution\n5. Ending",
                "image": "plot_mountain_diagram.jpg",
                "speakerNotes": {
                    "objective": "Visualize narrative shape and key stages.",
                    "questions": ["Where is the most exciting moment?"],
                    "imagePrompt": "Labeled story mountain with icons on each peak.",
                    "scaffoldingTips": {
                        "support": "Provide blank templates with sentence frames.",
                        "core": "Map a story the class knows.",
                        "challenge": "Add internal conflict alongside external."
                    },
                    "teacherTip": "Draw a real mountain if needed — students relate well to visual metaphors."
                }
            },
            {
                "title": "🔥 What Is Conflict?",
                "content": "Types of conflict:\n- Character vs Self\n- Character vs Character\n- Character vs Environment\n        Conflict creates questions.Resolution brings answers.",
                "image": "conflict_types_graphic.jpg",
                "speakerNotes": {
                    "objective": "Introduce conflict as a driver of story change.",
                    "questions": ["Which type of conflict feels most exciting to you?"],
                    "imagePrompt": "Three comic panels showing each conflict type.",
                    "scaffoldingTips": {
                        "support": "Match conflict types to book examples.",
                        "core": "Students write their own example.",
                        "challenge": "Write a scene with two conflict types at once."
                    },
                    "teacherTip": "Let students debate which is 'hardest' to overcome."
                }
            },
            {
                "title": "🤖 AI Example: Plot with a Problem",
                "content": "Prompt: ‘Create a story outline where a robot loses its job.’\n        AI Output: \n- Beginning: RoboX delivers packages\n - Conflict: RoboX is replaced by a new model\n - Resolution: He finds purpose helping lost pets\n - Ending: Starts ‘RescueBots’ club",
                "image": "ai_plot_outline.jpg",
                "speakerNotes": {
                    "objective": "Show how AI generates structured plot with conflict/resolution.",
                    "questions": ["What’s the emotional core of this story?"],
                    "imagePrompt": "Robot sitting with a ‘You’re Fired’ sign and puppy wagging tail.",
                    "aiPrompt": "Create a story where a character fails a test but discovers something new.",
                    "scaffoldingTips": {
                        "support": "Summarize each part on chart paper.",
                        "core": "Highlight rising action and resolution.",
                        "challenge": "Edit the resolution to make it bittersweet."
                    },
                    "teacherTip": "Ask: Would your story end the same way? Why or why not?"
                }
            },
            {
                "title": "🔧 Group Work: Build a Plot Mountain",
                "content": "1. Choose a conflict (AI or your own)\n2. Fill in each stage of the story\n3. Add a surprise ending\n4. Present or perform!",
                "image": "group_plot_mountain_activity.jpg",
                "speakerNotes": {
                    "objective": "Apply structure creatively with group scaffolding.",
                    "questions": ["What makes your ending satisfying?"],
                    "imagePrompt": "Teams gathered around story maps or whiteboards.",
                    "scaffoldingTips": {
                        "support": "Pre-fill first half of mountain.",
                        "core": "Use large chart to brainstorm.",
                        "challenge": "Add a subplot with a secondary conflict."
                    },
                    "teacherTip": "Time this carefully. Emphasize the build-up and resolution, not just problem."
                }
            },
            {
                "title": "🧠 Metacognitive Reflection: Was the Conflict Worth It?",
                "content": "Think back:\n- What did your character learn?\n- What surprised you about the ending?\n- What would you change if you rewrote it?",
                "image": "student_reflection_story.jpg",
                "speakerNotes": {
                    "objective": "Encourage reflection on narrative decisions.",
                    "questions": ["Did AI change how you thought about resolution?"],
                    "imagePrompt": "Notebook open to a ‘reflection’ page with lightbulb doodles.",
                    "scaffoldingTips": {
                        "support": "Sentence starter: ‘I didn’t expect…’.",
                        "core": "Partner reflection discussion.",
                        "challenge": "Rewrite the same story with opposite outcome."
                    },
                    "teacherTip": "Allow different reflection formats — writing, sketching, or verbal sharing."
                }
            },
            {
                "title": "📘 Homework: Choose Your Ending",
                "content": "1. Use AI to generate 2 possible endings for a story.\n2. Pick your favorite.\n3. Write the build-up that leads to that ending.\n4. AIDEN Journal: Why did this ending feel right?",
                "image": "homework_story_endings.jpg",
                "speakerNotes": {
                    "objective": "Practice narrative revision and closure skills.",
                    "questions": ["What makes an ending feel earned?"],
                    "imagePrompt": "Book pages split into ‘Ending A’ and ‘Ending B’.",
                    "scaffoldingTips": {
                        "support": "Offer pre-made endings to choose from.",
                        "core": "Students use AI to scaffold, then build the scene.",
                        "challenge": "Switch endings and revise the middle of the story instead."
                    },
                    "teacherTip": "Use this as prep for independent writing next week."
                }
            }
        ],
        "teacherMaterials": [
            {
                "name": "Lesson Plan & Objectives",
                "componentType": "lessonplanObjectives",
                "content": [
                    {
                        "sectionName": "Learning Objectives",
                        "title": "What Students Will Learn",
                        "description": "By the end of this lesson, students will:\n✅ Understand narrative structure using the plot mountain.\n✅ Create tension through conflict.\n✅ Generate and evaluate resolutions.\n✅ Use AI to scaffold plot structure and explore multiple directions.",
                        "icon": "TargetIcon"
                    }
                ],
                "lessonPlan": {
                    "sections": [
                        {
                            "part": 1,
                            "name": "Starter: Story Mash-Up",
                            "duration": 5,
                            "activities": [
                                {
                                    "icon": "GroupIcon",
                                    "primary": "Predict story problems from random story elements.",
                                    "secondary": "Activate creative thinking using constraints."
                                }
                            ]
                        },
                        {
                            "part": 2,
                            "name": "Concept: Plot Structure + Conflict Types",
                            "duration": 10,
                            "activities": [
                                {
                                    "icon": "PresentationIcon",
                                    "primary": "Explain plot stages using a mountain visual.",
                                    "secondary": "Use real story examples for each stage."
                                }
                            ]
                        },
                        {
                            "part": 3,
                            "name": "AI Demonstration: Outline with Conflict + Resolution",
                            "duration": 10,
                            "activities": [
                                {
                                    "icon": "AIIcon",
                                    "primary": "Use AI to generate plot and test alternate endings.",
                                    "secondary": "Discuss emotional impact of each version."
                                }
                            ]
                        },
                        {
                            "part": 4,
                            "name": "Group Project: Build a Plot Mountain",
                            "duration": 15,
                            "activities": [
                                {
                                    "icon": "CollaborationIcon",
                                    "primary": "Teams create full story structures.",
                                    "secondary": "Present or perform ideas to peers."
                                }
                            ]
                        },
                        {
                            "part": 5,
                            "name": "Plenary: Reflection & Meaning",
                            "duration": 5,
                            "activities": [
                                {
                                    "icon": "DiscussionIcon",
                                    "primary": "Students reflect on choices and emotional arc.",
                                    "secondary": "Explore how AI contributed."
                                }
                            ]
                        }
                    ]
                }
            },
            {
                "name": "Assessment & Homework",
                "componentType": "assessment",
                "content": [
                    {
                        "sectionName": "Assessment Criteria",
                        "title": "Evaluating Narrative Structure",
                        "description": "Students will be assessed on their ability to:\n✔️ Develop plot with tension and resolution.\n✔️ Build character action that reveals motive.\n✔️ Compare human vs AI-generated endings.\n✔️ Reflect on emotional resonance of story.\n✔️ Work collaboratively in planning and presentation.",
                        "icon": "CheckIcon"
                    },
                    {
                        "sectionName": "Homework Task",
                        "title": "Pick + Build a Story Ending",
                        "description": "Generate 2 endings using AIDEN GPT. Pick one and write a build-up that leads into that ending.\nReflect: What does your ending reveal about your character’s journey?",
                        "icon": "BookIcon"
                    }
                ]
            },
            {
                "name": "Metacognitive Reflection",
                "componentType": "metacognitiveExercise",
                "content": [
                    {
                        "sectionName": "In-Class Prompt",
                        "title": "Why Conflict Matters",
                        "description": "Students complete sentence: ‘The conflict in our story showed that…’.\nSticky-note wall share or quick pair presentations.",
                        "icon": "ReflectionIcon"
                    },
                    {
                        "sectionName": "AIDEN Journal Entry",
                        "title": "Plot Decisions + Emotional Impact",
                        "description": "Students reflect on endings:\n- Which version felt more real?\n- What would happen if the story didn’t resolve at all?\n- How did tension build reader interest?",
                        "icon": "NotebookIcon"
                    }
                ]
            }
        ]
    },
    {
        "metadata": {
            "id": "english_ks2_week2B",
            "classTitle": "English",
            "keyStage": 2,
            "week": 2,
            "order": 4,
            "module": "Narrative Writing",
            "subheading": "Narrative Writing",
            "lesson": "Week 2B",
            "yearGroup": "Year 6",
            "title": "Descriptive Language & Tone",
            "description": "Explore how word choice and tone influence emotional impact in storytelling.",
            "curriculumAlignment": "KS2 Writing – Vocabulary, Grammar and Punctuation: Use rich and varied vocabulary and a range of sentence structures to convey emotion and tone. KS2 Reading – Comprehension: Identify how language, structure, and presentation contribute to meaning."
        },
        "slides": [
            {
                "title": "🎨 Welcome to Tone Lab!",
                "content": "Words are not just information — they create feeling. Today we’ll explore how writers use description to build emotion and energy in scenes.",
                "image": "tone_intro_emotion_palette.jpg",
                "speakerNotes": {
                    "objective": "Introduce tone as a key element in narrative craft.",
                    "questions": ["How do you want your story to feel for your reader?"],
                    "imagePrompt": "Paint palette with words like ‘calm’, ‘angry’, ‘mysterious’ instead of colors.",
                    "scaffoldingTips": {
                        "support": "Use emojis to anchor tone: 😊 😠 😱",
                        "core": "Ask: Which stories made you feel something strong?",
                        "challenge": "Define tone vs mood."
                    },
                    "teacherTip": "Use expressive reading tone to model contrast."
                }
            },
            {
                "title": "🔍 Starter: Tone Detective",
                "content": "Read two versions:\n1. ‘The wind moved through the trees.’\n2. ‘The wind howled, snapping branches like bones.’\n        What’s the tone?", "image": "wind_tone_comparison.jpg",
                "speakerNotes": {
                    "objective": "Help students detect tone shifts through descriptive choices.",
                    "questions": ["Which feels calm? Which feels scary? Why?"],
                    "imagePrompt": "Two versions of forest scene side by side.",
                    "scaffoldingTips": {
                        "support": "Act it out with sound effects.",
                        "core": "Match word to tone on board.",
                        "challenge": "Add a third rewrite in humorous tone."
                    },
                    "teacherTip": "Collect student synonyms for tone words."
                }
            },
            {
                "title": "🧠 What Is Tone in Writing?",
                "content": "Tone = How the writing feels\nIt’s built through:\n- Sensory language\n- Figurative devices (simile, metaphor)\n- Word choice & punctuation\n- Pace and sentence style",
                "image": "tone_definition_chart.jpg",
                "speakerNotes": {
                    "objective": "Explain how writers control tone through language tools.",
                    "questions": ["What makes a sentence sound excited vs nervous?"],
                    "imagePrompt": "Diagram of sentence components labelled by tone effect.",
                    "scaffoldingTips": {
                        "support": "Provide example similes: ‘like fire’, ‘like whispers’.",
                        "core": "Underline adjectives and verbs in model text.",
                        "challenge": "Rewrite passive sentence to build stronger tone."
                    },
                    "teacherTip": "Use highlighter colours to code for tone."
                }
            },
            {
                "title": "🤖 AI Rewrite: One Scene, Many Tones",
                "content": "Prompt: ‘Describe a character walking into a forest in a scared tone.’\n        Let’s try changing that to: \n- Joyful\n - Angry\n - Mysterious\n        Can AI match the feeling ? ",
                "image": "ai_tone_shift_demo.jpg",
                "speakerNotes": {
                    "objective": "Show how AI can generate variations and provoke comparison.",
                    "questions": ["Which version felt most real?"],
                    "imagePrompt": "Split screen: 3 versions of same scene in different styles.",
                    "aiPrompt": "Change tone to ‘mysterious’. Keep the setting and action the same.",
                    "scaffoldingTips": {
                        "support": "Provide fill-in-the-blank sentence starters.",
                        "core": "Students vote on most effective version.",
                        "challenge": "Ask students to write their own contrasting tone paragraph."
                    },
                    "teacherTip": "Pause to dissect WHY the tone changed — word by word."
                }
            },
            {
                "title": "🧑‍🤝‍🧑 Group Task: Scene Shaper",
                "content": "1. Your group gets a neutral scene.\n2. Rewrite it in TWO tones using description.\n3. Use AI to generate a third.\n4. Read aloud — class guesses your tone!",
                "image": "student_groups_editing_tone.jpg",
                "speakerNotes": {
                    "objective": "Activate peer collaboration and tone experimentation.",
                    "questions": ["How did you show your tone without saying it?"],
                    "imagePrompt": "Students reading tone-tagged scenes aloud.",
                    "scaffoldingTips": {
                        "support": "Offer tone word banks.",
                        "core": "Use a checklist for editing description.",
                        "challenge": "Incorporate two contrasting tones in one passage."
                    },
                    "teacherTip": "Model tone guessing with your own sample first."
                }
            },
            {
                "title": "🧠 Reflection: What Did Tone Change?",
                "content": "Tone changes EVERYTHING:\n- Emotion\n- Reader expectation\n- Scene pace\n        Prompt: What was the hardest part of rewriting tone today?",
                "image": "student_journaling_tone_effect.jpg",
                "speakerNotes": {
                    "objective": "Support emotional and critical reflection on writing impact.",
                    "questions": ["What tone came easiest? Why?"],
                    "imagePrompt": "Journal page with arrows showing mood shifts.",
                    "scaffoldingTips": {
                        "support": "Provide emotion icons to choose from.",
                        "core": "Sentence starter: ‘I found it hard to… because…’.",
                        "challenge": "Ask: Could your tone mislead the reader on purpose?"
                    },
                    "teacherTip": "Link tone reflection back to plot meaning."
                }
            },
            {
                "title": "📘 Homework: Rewrite the Mood",
                "content": "1. Use AI to rewrite a paragraph in 3 tones:\n- Angry\n- Calm\n- Excited\n2. AIDEN Journal: Which version felt most like you?\nWhich was hardest to get right?",
                "image": "homework_tone_variations.jpg",
                "speakerNotes": {
                    "objective": "Extend creative experimentation and self-awareness.",
                    "questions": ["What tone surprised you the most?"],
                    "imagePrompt": "Notebook with three tone labels and colorful highlights.",
                    "scaffoldingTips": {
                        "support": "Provide sample paragraph to edit.",
                        "core": "Checklist: verbs, adjectives, sensory imagery.",
                        "challenge": "Ask: Could you write a story where the tone tricks the reader?"
                    },
                    "teacherTip": "Celebrate effort in risk-taking with tone."
                }
            }
        ],
        "teacherMaterials": [
            {
                "name": "Lesson Plan & Objectives",
                "componentType": "lessonplanObjectives",
                "content": [
                    {
                        "sectionName": "Learning Objectives",
                        "title": "What Students Will Learn",
                        "description": "By the end of this lesson, students will:\n✅ Use descriptive language to shape tone.\n✅ Identify and revise tone using figurative devices.\n✅ Compare AI-generated tone with human writing.\n✅ Reflect on emotional meaning in narrative writing.",
                        "icon": "TargetIcon"
                    }
                ],
                "lessonPlan": {
                    "sections": [
                        {
                            "part": 1,
                            "name": "Starter: Tone Detective",
                            "duration": 5,
                            "activities": [
                                {
                                    "icon": "CompareIcon",
                                    "primary": "Identify tone from two similar sentences.",
                                    "secondary": "Discuss word choice and emotion."
                                }
                            ]
                        },
                        {
                            "part": 2,
                            "name": "Concept: What is Tone?",
                            "duration": 10,
                            "activities": [
                                {
                                    "icon": "PresentationIcon",
                                    "primary": "Introduce tone-building tools.",
                                    "secondary": "Use rewritten paragraph models."
                                }
                            ]
                        },
                        {
                            "part": 3,
                            "name": "AI Demonstration: Tone Shifting",
                            "duration": 10,
                            "activities": [
                                {
                                    "icon": "AIIcon",
                                    "primary": "Use AI to generate tone variations.",
                                    "secondary": "Evaluate which feels most fitting."
                                }
                            ]
                        },
                        {
                            "part": 4,
                            "name": "Group Writing Task: Scene Shaper",
                            "duration": 15,
                            "activities": [
                                {
                                    "icon": "CollaborationIcon",
                                    "primary": "Students rewrite scenes with varying tone.",
                                    "secondary": "Read aloud and guess tone."
                                }
                            ]
                        },
                        {
                            "part": 5,
                            "name": "Plenary: Reflection & Journaling",
                            "duration": 5,
                            "activities": [
                                {
                                    "icon": "NotebookIcon",
                                    "primary": "Students reflect on tone experimentation.",
                                    "secondary": "Prompt: What tone was hardest to write and why?"
                                }
                            ]
                        }
                    ]
                }
            },
            {
                "name": "Assessment & Homework",
                "componentType": "assessment",
                "content": [
                    {
                        "sectionName": "Assessment Criteria",
                        "title": "Evaluating Descriptive Tone",
                        "description": "Students will be assessed on their ability to:\n✔️ Use precise vocabulary and description to create tone.\n✔️ Distinguish and revise writing by tone type.\n✔️ Use AI to experiment and refine stylistic choices.\n✔️ Reflect on tone’s narrative purpose in journals.",
                        "icon": "CheckIcon"
                    },
                    {
                        "sectionName": "Homework Task",
                        "title": "Rewrite in 3 Tones",
                        "description": "Students rewrite a paragraph in angry, calm, and excited tones.\nThey reflect in their AIDEN Journal: Which felt most authentic? What changed most in the writing?",
                        "icon": "BookIcon"
                    }
                ]
            },
            {
                "name": "Metacognitive Reflection",
                "componentType": "metacognitiveExercise",
                "content": [
                    {
                        "sectionName": "In-Class Prompt",
                        "title": "Tone & Truth",
                        "description": "Complete the sentence: ‘My tone didn’t match my meaning when…’.\nSmall group share followed by sticky note gallery.",
                        "icon": "ReflectionIcon"
                    },
                    {
                        "sectionName": "AIDEN Journal Entry",
                        "title": "How I Shaped Emotion Today",
                        "description": "Students reflect on tone practice:\n- Which scene came to life?\n- How did AI help or limit your emotional voice?\n- What will you try next time to enhance tone?",
                        "icon": "NotebookIcon"
                    }
                ]
            }
        ]
    }


]